import { createContext, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  getCurrentUserProjectPermissions,
  getGroupDetails,
  GetGroupDetailsResponse,
} from "../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../service/Shared";

type GroupPageRouteParams = "groupUuid";

interface GroupProviderProps {
  children?: JSX.Element;
}

interface GroupContextType {
  groupDetails: GetGroupDetailsResponse | undefined;
  groupType: string | undefined;
  hasGroupPermission: (permission: string) => boolean;
  origin: string | null;
  currentProjectUuid: string | null;
  errors: ServiceError[] | undefined;
  refreshGroup: boolean;
  setRefreshGroup: Dispatch<SetStateAction<boolean>>;
}

export const GroupContext = createContext<GroupContextType>({} as GroupContextType);

export const GroupProvider = ({ children }: GroupProviderProps): JSX.Element => {
  const { groupUuid } = useParams<GroupPageRouteParams>();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<ServiceError[] | undefined>([]);
  const [refreshGroup, setRefreshGroup] = useState<boolean>(false);
  const [groupDetails, setGroupDetails] = useState<GetGroupDetailsResponse | undefined>(undefined);
  const [groupPermissions, setGroupPermissions] = useState<string[]>([]);

  const origin = searchParams.get("origin");
  const currentProjectUuid = searchParams.get("projectUuid");

  const fetchData = useCallback(async () => {
    if (groupUuid) {
      const [groupDetailsRes, permissionsRes] = await Promise.all([
        getGroupDetails({ projectGroupUuid: groupUuid }),
        getCurrentUserProjectPermissions({ projectUuids: null, groupUuids: [groupUuid] }),
      ]);

      if (groupDetailsRes.status === Status.Success && groupDetailsRes.data) {
        setGroupDetails(groupDetailsRes.data);
      } else {
        setErrors(groupDetailsRes.errors);
      }

      if (permissionsRes.status === Status.Success && permissionsRes.data) {
        setGroupPermissions(permissionsRes.data[0]?.currentUserPermissions ?? []);
      }
    }
  }, [groupUuid]);

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData, groupUuid, refreshGroup]);

  const hasGroupPermission = (permission: string): boolean => groupPermissions.includes(permission);

  const memoisedValue = useMemo(
    () => ({
      groupDetails,
      groupType: groupDetails?.type,
      hasGroupPermission,
      origin,
      currentProjectUuid,
      errors,
      refreshGroup,
      setRefreshGroup,
    }),
    [groupDetails, groupPermissions, errors, fetchData, refreshGroup]
  );

  return <GroupContext.Provider value={memoisedValue}>{!isLoading && children}</GroupContext.Provider>;
};
